// Adds an alert dot to the browser tab favicon (e.g. the session timeout warning) so users on another tab still notice it.

export class FaviconBadger {
  static BADGE_SIZE = 32;
  private originalFavicons: Map<HTMLLinkElement, string> | null = null;
  private baseImage: Promise<HTMLImageElement> | null = null;
  private alertVisible = false;
  private renderId = 0;

  showAlert(): void {
    this.alertVisible = true;
    this.render();
  }

  hideAlert(): void {
    this.alertVisible = false;
    this.render();
  }

  private render(): void {
    const renderId = ++this.renderId;
    const favicons = this.getOriginalFavicons();

    if (!this.alertVisible) {
      favicons.forEach((href, link) => { link.href = href; });
      return;
    }

    this.drawBadge().then((href) => {
      if (renderId !== this.renderId || !href) return;
      favicons.forEach((_, link) => { link.href = href; });
    });
  }

  // Remembers the page's own favicons the first time a badge is shown, so they can be put back later
  private getOriginalFavicons(): Map<HTMLLinkElement, string> {
    if (!this.originalFavicons) {
      const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
      this.originalFavicons = new Map(links.map(link => [link, link.href]));
    }
    return this.originalFavicons;
  }

  // Draws the original favicon with a red dot in the corner
  private async drawBadge(): Promise<string | null> {
    const image = await this.loadBaseImage().catch(() => null);
    const canvas = document.createElement('canvas');
    canvas.width = FaviconBadger.BADGE_SIZE;
    canvas.height = FaviconBadger.BADGE_SIZE;
    const context = canvas.getContext('2d');
    if (!context) return null;

    if (image) context.drawImage(image, 0, 0, FaviconBadger.BADGE_SIZE, FaviconBadger.BADGE_SIZE);

    const radius = FaviconBadger.BADGE_SIZE * 0.26;
    const center = FaviconBadger.BADGE_SIZE - radius - 1;
    context.beginPath();
    context.arc(center, center, radius, 0, 2 * Math.PI);
    context.fillStyle = '#dc3545';
    context.fill();
    context.lineWidth = 2;
    context.strokeStyle = '#ffffff';
    context.stroke();

    return canvas.toDataURL('image/png');
  }

  // Loads the sharpest original favicon once and reuses it for every badge
  private loadBaseImage(): Promise<HTMLImageElement> {
    if (!this.baseImage) {
      const links = Array.from(this.getOriginalFavicons().entries());
      const source = links.find(([link]) => link.sizes?.value === `${FaviconBadger.BADGE_SIZE}x${FaviconBadger.BADGE_SIZE}`) ?? links[0];
      this.baseImage = new Promise((resolve, reject) => {
        if (!source) return reject(new Error('No favicon found'));

        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = source[1];
      });
    }
    return this.baseImage;
  }
}
